import { useEditor } from "novel";
import { convertJSONContentToFormattedText, generateUUID, getStringWordCount } from "@/utils";
import { useTiptapContext } from "../tiptap-provider";
import { findAiTaskRanges, lastPosOfAiTask, EditorNode } from "novel";
import { ChevronDownIcon, CirclePlayIcon, GripVerticalIcon, Trash2Icon } from "lucide-react";
import { useCallback, useMemo, useRef, useState, type MouseEvent } from "react";
import { useAllModels, useGetGenerateProviderByModel } from "@/stores/llmConnectionStore";
import { usePromptModelGroupsByType, useSelectedPromptByType } from "@/stores/novelCoreStore";
import { InputValueType, NovelActSchema, NovelChapterSchema, NovelSceneSchema, PromptMessage, PromptType } from "#/index";
import { Dropdown, message } from "antd";
import { PromptInputComponents } from "@/components/inputs";
import { getNovelActTree, PromptRenderer } from "@/plugins/prompt-engine";
import { generateStore } from "@/stores/generateStore";
import { assertCanGenerate, deductAfterGenerate, estimateTokensFromMessages } from "@/lib/thesisQuota";
import { thesisApiBase, THESIS_MODEL_NAME } from "@/lib/thesisPlatform";
import { AIServiceType, aiProviderFactory } from "@/ai";
import {
  assertLiveChapterAccess,
  getLiveThesisSystemPrompt,
} from "@/lib/thesisFramework/livePrompt";
import { THESIS_ACTIONS, loadGenerateExtras } from "@/lib/thesisGenerateExtras";
import { useSubscriptionStore } from "@/stores/subscriptionStore";

interface AiGenerationModel {
  id: string;
  model: string;
}

type AiGenerationInputs = Record<string, Array<{ value: string; type: InputValueType }>>;
interface AiGenerationAttributes {
  collapsed: boolean;
  prompt?: string;
  model?: AiGenerationModel;
  inputs?: AiGenerationInputs;
}
type AiGenerationNodeProps = {
  handleDragStart: () => void;
  deleteNode: () => void;
  clearDownNode: () => void;
  getPos: () => number;
  children: React.ReactNode;
  node: EditorNode;
  attributes: AiGenerationAttributes;
  updateAttributes: (attrs: Partial<AiGenerationAttributes>) => void;
}

const aiMessage = () => {
  return [{
    role: 'system',
    content: `你是 MBA 论文写作助手，辅助学生完成分章撰写，不是代写枪手。
规则：
- 使用规范学术中文；论证清楚，术语前后一致。
- 服从开题卡中的研究问题、理论镜头、构念与研究边界。
- 只写当前提示要求的内容，不抢写后章，不编造文献或数据。
- 避免空话套话；结论须可回溯到前文论据。
- 提示完成即停，不要自行续写额外段落。`,
  },
  {
    role: 'user',
    content: `按以下说明接着写约 200 字学术正文：
<说明>
承接上文，围绕本章论证契约往下写，不要超出提示范围。
</说明>`,
  }]
}

const AiGenerationNode = ({
  handleDragStart,
  deleteNode,
  getPos,
  children,
  node,
  attributes,
  updateAttributes
}: AiGenerationNodeProps) => {
  const { editor } = useEditor()
  const nodeRef = useRef<HTMLDivElement>(null)
  const { tasks, addAiTask, updateAiTaskStatus, appendAiTaskWordCount } = useTiptapContext(state => state)
  const models = useAllModels()
  const [formData, setFormData] = useState<AiGenerationAttributes>(attributes)
  const getGenerateProvider = useGetGenerateProviderByModel()
  const [currentPrompt, setCurrentPrompt] = useState<{
    promptId: string;
    modelId: string;
    modelName: string;
  }>({
    promptId: attributes.prompt || '',
    modelId: attributes.model?.id || '',
    modelName: attributes.model?.model || '',
  })
  const [inputValues, setInputValues] = useState<AiGenerationInputs>(attributes.inputs || {})

  const sceneBeatPrompt = useSelectedPromptByType(PromptType.SceneBeatCompletion, currentPrompt.promptId)
  const modelGroups = usePromptModelGroupsByType(PromptType.SceneBeatCompletion)
  const currentModelGroups = useMemo(() => modelGroups?.find(group => group.key === sceneBeatPrompt?.id)?.children, [sceneBeatPrompt, modelGroups])
  const chatModel = useMemo(() => {
    const groups = modelGroups?.find(group => group.key === sceneBeatPrompt?.id)?.children
    const models = groups?.map(d => d.children).flat()
    return models?.find(model => model.internalId === currentPrompt.modelId) || models?.[0]
  }, [sceneBeatPrompt, modelGroups, currentPrompt])


  const nodeContent = useMemo(() => {
    const result: string[] = []
    node.content.forEach(item => {
      result.push(convertJSONContentToFormattedText(item.toJSON()))
    })
    return result.map(d => d.trim()).filter(Boolean).join('\n')
  }, [node.content])


  const actionId = loadGenerateExtras().action
  const actionLabel = THESIS_ACTIONS.find((a) => a.id === actionId)?.label || '接着写'
  /** 编审/润色写进块内，可折叠；续写类写到块后进正文 */
  const writeInside = actionId === 'review' || actionId === 'polish'

  const foldNode = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    updateAttributes({ collapsed: !attributes.collapsed })
  }

  const handleFormChange = (data: Partial<AiGenerationAttributes>) => {
    setFormData(prev => ({ ...prev, ...data }))
  }

  const handleModelChange = useCallback((internalId: string, modelName: string, promptId: string) => {
    setCurrentPrompt({ promptId, modelId: internalId, modelName })
  }, [setCurrentPrompt])
  const handleInputChange = useCallback((inputId: string, values?: { value: string; type: InputValueType }[]) => {
    setInputValues(prev => ({ ...prev, [inputId]: values || [] }))
  }, [setInputValues])

  const ensureTaskParagraph = useCallback((taskId: string) => {
    if (!editor) return;
    // If task has no paragraph yet, insert one at selection end
    const ranges = findAiTaskRanges(editor, taskId);
    if (!ranges) {
      editor.chain().focus().insertContent({ type: "paragraph", attrs: { aiTaskId: taskId }, content: [] }).run();
    }
  }, [editor]);

  const appendChunkToTask = useCallback((taskId: string, chunk: string) => {
    if (!editor) return;
    ensureTaskParagraph(taskId);

    const parts = chunk.split(/\n+/);
    parts.forEach((part, idx) => {
      // Move selection to end of task
      const end = lastPosOfAiTask(editor, taskId);
      if (end == null) return;

      editor
        .chain()
        .focus()
        .setTextSelection(end)
        .setAiPhaseWithTaskId("generating", taskId)
        .insertContent(part)
        .run();

      if (idx < parts.length - 1) {
        // New paragraph for same task
        editor.chain().focus().insertContent({ type: "paragraph", attrs: { aiTaskId: taskId } }).run();
      }
    });
  }, [editor, ensureTaskParagraph]);

  const finalizeTask = useCallback((taskId: string) => {
    if (!editor) return;
    const ranges = findAiTaskRanges(editor, taskId);
    if (!ranges) {
      updateAiTaskStatus(taskId, "generated")
      return
    }
    editor
      .chain()
      .focus()
      .setTextSelection({ from: ranges.from, to: ranges.to })
      .setAiPhaseWithTaskId("generated", taskId)
      .run();
    updateAiTaskStatus(taskId, "generated")
  }, [editor, updateAiTaskStatus]);


  const startAiTask = useCallback((messages: PromptMessage[]) => {
    if (!assertCanGenerate()) return
    const access = assertLiveChapterAccess()
    if (!access.ok) {
      message.warning(access.message)
      return
    }

    const platform = !!thesisApiBase()
    let provider = chatModel ? getGenerateProvider(chatModel.id) || getGenerateProvider(chatModel.name) : undefined
    let modelName = chatModel?.name || ''

    // 有代付 API 时优先走论文台连接，不依赖用户配 Key
    if (platform) {
      const jwt = useSubscriptionStore.getState().token
      provider = aiProviderFactory.createProvider({
        aiServiceType: AIServiceType.OpenAI,
        baseUrl: `${thesisApiBase()}/v1`,
        apiKey: jwt || 'thesis-platform',
      })
      modelName = THESIS_MODEL_NAME
    }

    if (!provider || (!platform && !chatModel)) {
      message.error(platform ? '代付服务不可用' : '请选择模型或配置 VITE_THESIS_API_BASE')
      return
    }

    const mapped = [
      { role: 'system' as const, content: getLiveThesisSystemPrompt() },
      ...messages.map((m) => ({
        role: m.type as 'user' | 'assistant' | 'system',
        content: m.text,
      })),
    ]

    const id = generateUUID();
    let out = ''
    const insertAt = writeInside
      ? getPos() + 1 // 块内首段
      : getPos() + node.nodeSize // 块后正文

    // 编审写进块内时先展开，否则看不见流式结果
    if (writeInside && attributes.collapsed) {
      updateAttributes({ collapsed: false })
    }

    editor?.chain()
      .focus(insertAt)
      .setAiMark({ phase: "idle", taskId: id, text: " " })
      .run();
    addAiTask({
      id,
      status: 'generating',
      wordCount: 0,
    })
    provider.sendStreamingRequest({
      model: modelName,
      messages: mapped,
    }, (chunk) => {
      out += chunk.text
      appendChunkToTask(id, chunk.text)
      appendAiTaskWordCount(id, chunk.text)
    }, id).then(() => {
      finalizeTask(id)
      const n = deductAfterGenerate({
        promptTokens: estimateTokensFromMessages(mapped),
        completionText: out,
      })
      if (writeInside) {
        message.success(n > 0 ? `已扣约 ${n} Token，可点「折叠」收起` : '完成，可点「折叠」收起', 2)
      } else if (n > 0) {
        message.info(`已扣约 ${n} Token，点「应用」确认采纳`, 2.5)
      } else {
        message.success('生成完成，点「应用」确认采纳', 2)
      }
    }).catch((err) => {
      updateAiTaskStatus(id, 'discarded')
      message.error(err instanceof Error ? err.message : '生成失败')
    })
  }, [editor, addAiTask, getPos, node, appendChunkToTask, finalizeTask, chatModel, getGenerateProvider, appendAiTaskWordCount, updateAiTaskStatus, writeInside, attributes.collapsed, updateAttributes])

  // 获取该节点之前的内容，获取该节点之后的内容
  const getNodeContext = useCallback(() => {
    if (!editor) return { before: undefined, after: undefined };

    const nodePos = getPos();
    const nodeSize = node.nodeSize;
    const nodeEndPos = nodePos + nodeSize;
    const docSize = editor.state.doc.content.size;

    // 获取节点之前的内容（从文档开始到节点位置）
    // const beforeText = editor.state.doc.textBetween(0, nodePos, '\n');
    const beforeText = editor.state.doc.slice(0, nodePos);

    // 获取节点之后的内容（从节点结束位置到文档结束）
    // const afterText = editor.state.doc.textBetween(nodeEndPos, docSize, '\n');
    const afterText = editor.state.doc.slice(nodeEndPos, docSize);

    return {
      before: beforeText.toJSON(),
      after: afterText.toJSON()
    };
  }, [editor, getPos, node]);

  const handleStart = useCallback(() => {
    const getSceneId = () => {
      let current = nodeRef.current?.parentElement
      while (current) {
        if (current.dataset.sceneId) {
          return current.dataset.sceneId
        }
        current = current.parentElement
      }
      return null
    }
    const messages: PromptMessage[] = []
    // 递归获取父元素的data-scene-id
    const sceneId = getSceneId()
    const actsTree = sceneId ? getNovelActTree(generateStore.getState().novelActsTree, { sceneIds: [sceneId] }) : undefined
    const actData = actsTree?.[0]
    const chapterData = actData?.children?.[0]
    const sceneData = chapterData?.children?.[0]
    const inputs = inputValues
    // 用户没有使用input,就使用input默认输入
    Object.values(sceneBeatPrompt?.inputs || {}).forEach(input => {
      if (!inputs[input.id] && input.default) {
        inputs[input.id] = input.default
      }
    })
    const nodeContext = getNodeContext()
    try {
      sceneBeatPrompt?.attributes?.messages.forEach(message => {
        messages.push({
          ...message,
          text: PromptRenderer.renderTemplate(message.text, {
            generateStore: generateStore,
            currentPosition: {
              message: node.content.toJSON(),
              novel: generateStore.getState().novel,
              sceneSoFar: nodeContext.before,
              sceneToCome: nodeContext.after,
              prompt: sceneBeatPrompt,
              inputs: inputs,
              act: actData?.data as NovelActSchema,
              chapter: chapterData?.data as NovelChapterSchema,
              scene: sceneData?.data as NovelSceneSchema,
            },
          })
        })
      })
      if (messages.length > 0) {
        startAiTask(messages)
      }
    } catch (error) {
      console.error('prompt render error', error)
    }

  }, [sceneBeatPrompt, inputValues, startAiTask])

  const platformMode = !!thesisApiBase()

  const PromptChoiceMenu = useCallback(() => {
    // 代付模式下模型固定，不展示可切换下拉（避免假换模型）
    if (platformMode) return null
    return <Dropdown
      placement="topLeft"
      trigger={['click']}
      menu={{
        rootClassName: "max-h-[300px] overflow-auto",
        items: modelGroups.map(prompt => ({
          label: <span className="flex items-center gap-4 justify-between">
            <span>{prompt.label}</span>
            {prompt.isSystem && <span className="text-blue-500">System</span>}
          </span>,
          key: prompt.key,
          children: prompt.children.length === 1 ?
            prompt.children[0].children.map(model => ({
              label: model.name,
              key: prompt.key + ':' + model.internalId,
              onClick: () => handleModelChange(model.internalId, model.name, prompt.key),
            }))
            :
            prompt.children.map((child, index) => [
              { type: 'divider', className: index === 0 ? 'hidden' : '' },
              {
                type: 'group',
                label: <span className="text-ant-text font-bold text-xs">{child.label}</span>,
              },
              ...child.children.map(model => ({
                label: model.name,
                key: prompt.key + ':' + model.internalId,
                onClick: () => handleModelChange(model.internalId, model.name, prompt.key),
              })),
            ]).flat(),
        })),
      }
      }
    >
      <div className="generate-prompts border-2 border-gray-400/50 rounded-r-md flex items-center px-1 text-gray-500 hover:border-gray-400/100">
        <ChevronDownIcon className="size-5" />
      </div>
    </Dropdown>
  }, [modelGroups, handleModelChange, platformMode])


  const collapsed = !!attributes.collapsed

  return (
    <div className="tt-nc-block-element my-4 border-2 border-primary-border rounded-lg bg-slate-300/20" ref={nodeRef}>
      <div contentEditable={false} className={`bg-slate-300/50 select-none flex justify-between items-center ${collapsed ? 'rounded-lg' : 'rounded-t-lg'} py-2 px-2`}>
        <div className="flex items-center gap-2">
          <button type="button" className="ai-generation-drag-handle cursor-grab" onMouseDown={handleDragStart}>
            <GripVerticalIcon />
          </button>
          <span className="font-bold">{actionLabel}</span>
          {!collapsed && (
            <span className="text-xs text-gray-500 hidden sm:inline">点右侧播放生成</span>
          )}
          {collapsed && (
            <span className="text-xs text-gray-500">已折叠</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="text-gray-500 hover:text-gray-700 cursor-pointer text-sm"
            onClick={foldNode}
          >
            {collapsed ? '展开' : '折叠'}
          </button>
          <div className="border-l-2 border-slate-300 h-4" />
          <button
            type="button"
            className="text-gray-500 hover:text-gray-700 cursor-pointer p-1"
            onClick={deleteNode}
          >
            <Trash2Icon />
          </button>
        </div>
      </div>

      {/* 内容须留在 DOM（ProseMirror）；折叠只收起可视高度 */}
      <div
        className={collapsed ? 'overflow-hidden' : 'node-view-content mx-2 mb-2 mt-2'}
        style={collapsed ? { height: 0, margin: 0, padding: 0, opacity: 0 } : undefined}
        aria-hidden={collapsed}
      >
        {children}
      </div>
      {!collapsed && Object.values(sceneBeatPrompt?.inputs || {}).length > 0 && (
        <div
          contentEditable={false}
          className="prompt-inputs-wrapper bg-slate-300/20 border-slate-300 border-t-1 px-2 py-1 @container flex flex-wrap items-center gap-1.5"
        >
          {Object.values(sceneBeatPrompt?.inputs || {}).map(input => (
            <PromptInputComponents
              key={input.id}
              inputOption={input}
              value={inputValues[input.id] || input.default || []}
              onChange={(values) => handleInputChange(input.id, values)} />
          ))}
        </div>
      )}
      {!collapsed && (
        <div contentEditable={false} className="node-view-footer flex items-center flex-wrap bg-slate-300/20 border-slate-300 border-t-1 p-2">
          <div className="generate-wrapper flex">
            <div
              onClick={handleStart}
              className={`generate-start transition-all border-2 border-gray-400/50 cursor-pointer flex items-center gap-2 px-2 py-1 hover:border-gray-400/100 hover:bg-slate-300/10 ${
                platformMode ? 'rounded-md' : 'border-r-0 rounded-l-md'
              }`}
            >
              <CirclePlayIcon className="size-5" />
              <div className="text-xs font-bold text-gray-600">{actionLabel}</div>
            </div>
            <PromptChoiceMenu />
          </div>
        </div>
      )}
    </div>
  )
}

export default AiGenerationNode;